import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Hairline, Icon, RubberStamp, Swatch, type SwatchKind } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { supabase } from "@/integrations/supabase/client";
import { useBlockedUsers } from "@/hooks/useBlockedUsers";

// Brand-filtered view of all posted scans for a given brand name.
// Reached from /home → Trending Brands tap.
type BrandScan = {
  id: string;
  title: string;
  brand: string | null;
  era: string | null;
  price_suggested: number | null;
  confidence: number | null;
  verdict: string;
  img: string;
  photos: string[];
  user_id: string;
  caption: string | null;
  created_at: string;
  handle: string;
  display_name: string | null;
  avatar: string;
};

export default function Brand() {
  const nav = useNavigate();
  const { name } = useParams<{ name: string }>();
  const brandName = useMemo(() => decodeURIComponent(name ?? ""), [name]);

  const [scans, setScans] = useState<BrandScan[]>([]);
  const [loading, setLoading] = useState(true);
  const { data: blockedSet } = useBlockedUsers();

  useEffect(() => {
    let cancelled = false;
    if (!brandName) return;
    (async () => {
      setLoading(true);

      // Case-insensitive match — brands in the DB are user/AI-typed and may vary.
      // Pin-aware order: Find of the Week winners that still have an active
      // 7-day brand pin (pinned_to_brand_until > now()) sort to the top.
      // After 7 days the pin column is in the past, NULLS LAST drops them
      // back below unpinned recency-sorted rows.
      const { data: rows } = await supabase
        .from("scans")
        .select("id,title,brand,era,price_suggested,confidence,verdict,img,photos,user_id,caption,created_at,pinned_to_brand_until,is_weekly_feature_winner")
        .eq("posted", true)
        .is("suspended_at", null)
        .ilike("brand", brandName)
        .order("pinned_to_brand_until", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      const userIds = Array.from(new Set((rows ?? []).map((r) => r.user_id)));
      const { data: profiles } = userIds.length
        ? await (supabase as any)
            .from("profiles_public")
            .select("id,handle,display_name,avatar")
            .in("id", userIds)
        : { data: [] as { id: string; handle: string; display_name: string | null; avatar: string }[] };

      const profById = new Map(
        ((profiles ?? []) as Array<{ id: string; handle: string; display_name: string | null; avatar: string }>)
          .map((p) => [p.id, p] as const)
      );

      const enriched: BrandScan[] = (rows ?? []).map((r) => {
        const p = profById.get(r.user_id);
        return {
          id: r.id,
          title: r.title,
          brand: r.brand,
          era: r.era,
          price_suggested: r.price_suggested,
          confidence: r.confidence,
          verdict: r.verdict,
          img: r.img,
          photos: r.photos ?? [],
          user_id: r.user_id,
          caption: r.caption,
          created_at: r.created_at,
          handle: p?.handle ?? "anon",
          display_name: p?.display_name ?? null,
          avatar: p?.avatar ?? "olive",
        };
      });

      if (!cancelled) {
        setScans(enriched);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [brandName]);

  // Filter out scans authored by blocked users
  const visibleScans = useMemo(
    () => scans.filter((s) => !blockedSet?.has(s.user_id)),
    [scans, blockedSet]
  );

  const stats = useMemo(() => {
    const prices = visibleScans.map((s) => s.price_suggested).filter((p): p is number => p != null);
    const avg = prices.length ? Math.round(prices.reduce((a, b) => a + Number(b), 0) / prices.length) : null;
    const high = prices.length ? Math.max(...prices.map(Number)) : null;
    const low = prices.length ? Math.min(...prices.map(Number)) : null;
    return { avg, high, low, count: visibleScans.length };
  }, [visibleScans]);

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 80 }}>
        {/* Header */}
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Brand File</span>
          <div style={{ width: 30 }} />
        </div>

        <div style={{ padding: "10px 20px 0" }}>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>Filed under</span>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 44,
            color: "hsl(var(--ink))", letterSpacing: "-0.03em", lineHeight: 0.95, marginTop: 4,
          }}>
            {brandName}
          </div>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, color: "hsl(var(--ink-2))", marginTop: 6 }}>
            every published find under this label
          </div>
        </div>

        <div style={{ padding: "0 20px" }}>
          <Hairline style={{ margin: "14px 0" }} />
        </div>

        {/* Stat strip */}
        {!loading && stats.count > 0 && (
          <div style={{ padding: "0 20px 16px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[
              { l: "Finds", v: String(stats.count) },
              { l: "Avg", v: stats.avg != null ? `$${stats.avg}` : "—" },
              { l: "Range", v: stats.low != null && stats.high != null ? `$${stats.low}–${stats.high}` : "—" },
            ].map((t) => (
              <div key={t.l} style={{ border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))", padding: "8px 10px" }}>
                <div className="caps" style={{ color: "hsl(var(--rust))" }}>{t.l}</div>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 18, color: "hsl(var(--ink))", marginTop: 2 }}>
                  {t.v}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Listings */}
        <div style={{ padding: "0 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          {loading && (
            <div style={{ padding: "24px 0", textAlign: "center", fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
              opening the file…
            </div>
          )}
          {!loading && visibleScans.length === 0 && (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15, color: "hsl(var(--ink-2))" }}>
                Nothing filed under "{brandName}" yet.
              </div>
              <button onClick={() => nav("/scan")} className="caps" style={{
                background: "none", border: "none", marginTop: 8, cursor: "pointer",
                textDecoration: "underline", color: "hsl(var(--ink))",
              }}>
                Be the first to file one →
              </button>
            </div>
          )}
          {visibleScans.map((s) => {
            const img = s.photos[0] || s.img;
            const isUrl = typeof img === "string" && /^https?:\/\//.test(img);
            return (
              <div key={s.id} style={{ background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))" }}>
                <button
                  onClick={() => nav(`/profile/${encodeURIComponent("@" + s.handle)}`)}
                  style={{ padding: 0, border: "none", background: "none", cursor: "pointer", display: "block", width: "100%" }}
                >
                  {isUrl ? (
                    <div style={{ position: "relative", width: "100%", aspectRatio: "4/3", borderBottom: "1px solid hsl(var(--ink))", background: "hsl(var(--paper-3))" }}>
                      <img src={img as string} alt={s.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      {s.confidence != null && (
                        <div style={{ position: "absolute", top: 10, right: 10 }}>
                          <RubberStamp
                            label={s.verdict === "PASS" ? "VINTAGE" : "FAST FASH"}
                            sub={s.verdict === "PASS" ? "" : `${s.confidence}%`}
                            color={s.verdict === "PASS" ? "hsl(var(--rust))" : "hsl(var(--ink))"}
                            size={66}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <Swatch kind={(img as SwatchKind) || "olive"} style={{ width: "100%", aspectRatio: "4/3", borderBottom: "1px solid hsl(var(--ink))" }} />
                  )}
                </button>
                <div style={{ padding: "10px 12px 12px" }}>
                  <div className="caps" style={{ color: "hsl(var(--rust))" }}>{s.era ?? "—"}</div>
                  <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 17, color: "hsl(var(--ink))", lineHeight: 1.15, marginTop: 2 }}>
                    {s.title}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                    <button
                      onClick={() => nav(`/profile/${encodeURIComponent("@" + s.handle)}`)}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <Swatch kind={(s.avatar as SwatchKind) || "olive"} style={{ width: 20, height: 20, borderRadius: "50%", border: "1px solid hsl(var(--ink))" }} />
                      <span className="caps">@{s.handle}</span>
                    </button>
                    {s.price_suggested != null && (
                      <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 16, color: "hsl(var(--ink))" }}>
                        ${Number(s.price_suggested).toFixed(0)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </MobileFrame>
  );
}
